import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useCopilotTurn, { COPILOT_OPEN_EVENT } from '../hooks/useCopilotTurn';
import './CopilotPanel.css';

// The agentOS copilot dock (lower LEFT — the AgentDock owns the lower right).
//
// Text turns are pure Firestore: sending a message CREATES a queued aiMessage
// doc (rules enforce role/uid/status), the agentOS worker claims and drives
// it, and the triage stage, live plan card, step lights, and approval gate
// below are just onSnapshot rendering the same doc. No sockets, no REST.
//
// (Ported from the nventr-io platform. Kit adaptations: flat single-tenant
// scopes — aiSessions is a top-level collection; voice stays on the
// AgentDock, so this panel is text + attachments only.)
//
// - Attachments: text files (md/txt/csv/json/…) are read in the browser and
//   inlined into the message — binaries belong in datasets.
// - Navigation: the worker's navigate_app tool stamps `navigateTo` on the
//   assistant message and this panel routes; the panel folds into the FAB so
//   it doesn't cover the page it just opened.
// - The turn spine (session, messages, enqueue, approve) is the shared
//   useCopilotTurn hook — the AgentDock's voice uses the same one, so a
//   spoken "build me…" lands here as a plan card and the voice reads it back.
const STEP_DOT = {
  pending: '#64748b',
  running: '#f59e0b',
  done: '#34d399',
  error: '#f87171',
};

const TEXT_EXTS = /\.(txt|md|markdown|csv|tsv|json|log|ya?ml|xml|html?)$/i;
const ATTACH_CHAR_BUDGET = 6000; // aiMessage content maxLen is 8000

function StepRow({ step, depth = 0 }) {
  return (
    <>
      <div className="cp-step" style={{ paddingLeft: 10 + depth * 16 }}>
        <span
          className={`cp-dot${step.status === 'running' ? ' cp-dot-pulse' : ''}`}
          style={{ background: STEP_DOT[step.status] || '#64748b' }}
        />
        <span className="cp-step-tool">{step.tool}</span>
        {step.note && <span className="cp-step-note">{step.note}</span>}
        {typeof step.ms === 'number' && <span className="cp-step-ms">{(step.ms / 1000).toFixed(1)}s</span>}
      </div>
      {step.error && <div className="cp-step-err" style={{ paddingLeft: 26 + depth * 16 }}>{step.error}</div>}
      {(step.sub?.steps || []).map((s) => <StepRow key={`${step.id}-${s.id}`} step={s} depth={depth + 1} />)}
    </>
  );
}

function PlanCard({ msg, mine, onApprove }) {
  const plan = msg.plan;
  if (!plan) return null;
  const waiting = msg.status === 'awaiting_approval';
  return (
    <div className={`cp-plan${waiting ? ' cp-plan-waiting' : ''}`}>
      <div className="cp-plan-title">
        {plan.title || 'Plan'}
        {plan.replanned ? <span className="cp-plan-replan">revised ×{plan.replanned}</span> : null}
      </div>
      {plan.rationale && <div className="cp-plan-rationale">{plan.rationale}</div>}
      <div>
        {(plan.steps || []).map((s) => <StepRow key={s.id} step={s} />)}
      </div>
      {waiting && (
        <div className="cp-plan-actions">
          {mine ? (
            <>
              <button className="cs-btn cs-btn-sm" onClick={() => onApprove('approved')}>Approve &amp; run</button>
              <button className="cs-btn cs-btn-outline cs-btn-sm" onClick={() => onApprove('rejected')}>Reject</button>
            </>
          ) : (
            <span className="cs-help" style={{ fontSize: 12 }}>waiting for the requester's approval</span>
          )}
        </div>
      )}
    </div>
  );
}

export default function CopilotPanel() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  // Navigation mode: when the copilot routes the app, the panel folds into
  // the FAB (distinct accent style) so it doesn't cover the page it just
  // opened — the session stays hot; click re-expands.
  const [navPeek, setNavPeek] = useState(false);
  const [text, setText] = useState('');
  const [attachments, setAttachments] = useState([]); // [{ name, content }]
  const { me, messages, busy, error: sendErr, setError: setSendErr, enqueue, approve: approveTurn, newSession } = useCopilotTurn({ enabled: open });
  const fileRef = useRef(null);
  const mountedAtRef = useRef(Date.now());
  const navigatedRef = useRef(new Set());

  // Bare paths ("pipelines", "review") resolve from the root; absolute
  // paths pass through.
  const resolvePath = (p) => {
    const path = String(p || '').trim().replace(/^\.?\//, (m) => (m === '/' ? '/' : ''));
    if (!path) return '/';
    return path.startsWith('/') ? path : `/${path}`;
  };

  const collapseForNav = () => {
    setOpen((o) => {
      if (o) setNavPeek(true);
      return false;
    });
  };

  // The dock delegated a spoken request: open so the plan card is visible.
  useEffect(() => {
    const onOpen = () => { setNavPeek(false); setOpen(true); };
    window.addEventListener(COPILOT_OPEN_EVENT, onOpen);
    return () => window.removeEventListener(COPILOT_OPEN_EVENT, onOpen);
  }, []);

  const endRef = useRef(null);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  // The worker's navigate_app: fresh assistant messages carrying navigateTo
  // route the app (once per message; history never replays).
  useEffect(() => {
    for (const m of messages || []) {
      if (m.role !== 'assistant' || !m.navigateTo) continue;
      if (m.createdAt < mountedAtRef.current || navigatedRef.current.has(m.id)) continue;
      navigatedRef.current.add(m.id);
      collapseForNav();
      navigate(resolvePath(m.navigateTo));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages]);

  const addFiles = async (list) => {
    setSendErr('');
    for (const file of Array.from(list || [])) {
      const texty = TEXT_EXTS.test(file.name) || (file.type || '').startsWith('text/');
      if (!texty) {
        setSendErr(`${file.name}: only text files inline here — binaries go to datasets.`);
        continue;
      }
      const used = attachments.reduce((n, a) => n + a.content.length, 0);
      const room = ATTACH_CHAR_BUDGET - used;
      if (room <= 0) {
        setSendErr('Attachment budget for one message is full — send, then attach more.');
        break;
      }
      // eslint-disable-next-line no-await-in-loop
      const raw = await file.text();
      const content = raw.slice(0, room);
      setAttachments((prev) => [...prev, { name: file.name, content, truncated: raw.length > room }]);
    }
    if (fileRef.current) fileRef.current.value = '';
  };

  const composeContent = () => {
    const parts = [text.trim()];
    for (const a of attachments) {
      parts.push(`--- attached: ${a.name}${a.truncated ? ' (truncated)' : ''} ---\n${a.content}`);
    }
    return parts.filter(Boolean).join('\n\n').slice(0, 8000);
  };

  const send = async (e) => {
    e.preventDefault();
    const content = composeContent();
    if (!content) return;
    setSendErr('');
    if (busy) return;
    setText('');
    setAttachments([]);
    try {
      await enqueue(content);
    } catch (err) {
      setSendErr(err.message);
    }
  };

  // Suggestion chips from the worker: navigate / act (send as an instruction) / ask.
  const onSuggestion = (sg) => {
    if (sg.kind === 'navigate') { collapseForNav(); navigate(resolvePath(sg.value)); return; }
    if (busy) return;
    enqueue(sg.value).catch((err) => setSendErr(err.message));
  };

  const approve = (msgId) => (decision) => approveTurn(decision, msgId).catch((e) => setSendErr(e.message));

  return (
    <>
      <button
        className={`cp-fab${open ? ' cp-fab-open' : ''}${navPeek && !open ? ' cp-fab-nav' : ''}`}
        onClick={() => { setNavPeek(false); setOpen(!open); }}
        aria-label="Toggle copilot"
        title={navPeek && !open ? 'Copilot collapsed for navigation — click to re-open' : undefined}
      >
        {open ? '✕' : '◈'}
      </button>
      {open && (
        <div className="cp-panel">
          <div className="cp-head">
            <span>agentOS copilot</span>
            <div className="cp-head-actions">
              <button className="cs-btn cs-btn-outline cs-btn-sm" onClick={newSession}>New session</button>
            </div>
          </div>

          <div className="cp-msgs">
            {(messages || []).length === 0 && (
              <div className="cp-empty">
                Ask me to build, run, or explain anything here — “build a pipeline that summarizes the news and run
                it”, “take me to the review queue”. Type or attach text files. Plans that change things wait for
                your approval.
              </div>
            )}
            {(messages || []).map((m) => (
              <div key={m.id} className={`cp-msg cp-msg-${m.role}`}>
                <div className="cp-bubble">{m.content}</div>
                {m.role === 'user' && m.stage && ['planning', 'executing', 'awaiting_approval'].includes(m.status) && (
                  <div className="cp-stage">
                    <span className="cp-dot cp-dot-pulse" style={{ background: '#22d3ee' }} />
                    {m.stage}
                  </div>
                )}
                {m.role === 'assistant' && m.navigateTo && (
                  <div className="cp-navline"><span aria-hidden="true">→</span> Opened {resolvePath(m.navigateTo)}</div>
                )}
                {m.role === 'assistant' && m.externalUrl?.url && /^https:\/\//.test(m.externalUrl.url) && (
                  <div className="cp-navline"><span aria-hidden="true">→</span> <a href={m.externalUrl.url} target="_blank" rel="noopener noreferrer">{m.externalUrl.label || 'Open link'}</a></div>
                )}
                {m.role === 'assistant' && Array.isArray(m.suggestions) && m.suggestions.length > 0 && (
                  <div className="cp-suggest">
                    {m.suggestions.map((sg, i) => (
                      <button key={`${m.id}-${i}`} type="button" className={`cs-chip cp-chip cp-chip-${sg.kind}`} onClick={() => onSuggestion(sg)} title={sg.value}>{sg.label}</button>
                    ))}
                  </div>
                )}
                {m.role === 'user' && <PlanCard msg={m} mine={m.uid === me?.uid} onApprove={approve(m.id)} />}
                {m.status === 'error' && m.error && <div className="cp-step-err">{m.error}</div>}
              </div>
            ))}
            <div ref={endRef} />
          </div>

          {sendErr && <div className="cp-step-err" style={{ padding: '0 12px' }}>{sendErr}</div>}
          {attachments.length > 0 && (
            <div className="cp-attach-row">
              {attachments.map((a, i) => (
                <span key={`${a.name}-${i}`} className="cp-attach-chip">
                  📄 {a.name}{a.truncated ? ' ✂' : ''}
                  <button type="button" aria-label={`Remove ${a.name}`} onClick={() => setAttachments((prev) => prev.filter((_, j) => j !== i))}>×</button>
                </span>
              ))}
            </div>
          )}
          <form className="cp-form" onSubmit={send}>
            <input
              ref={fileRef}
              type="file"
              multiple
              accept=".txt,.md,.markdown,.csv,.tsv,.json,.log,.yaml,.yml,.xml,.html,text/*"
              style={{ display: 'none' }}
              onChange={(e) => addFiles(e.target.files)}
            />
            <button type="button" className="cp-attach-btn" title="Attach text files" onClick={() => fileRef.current?.click()}>📎</button>
            <input
              className="cp-input"
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={busy ? 'copilot is working…' : 'Tell the copilot what to do…'}
            />
            <button
              className="cs-btn cs-btn-sm"
              type="submit"
              disabled={busy || !(text.trim() || attachments.length)}
            >
              Send
            </button>
          </form>
        </div>
      )}
    </>
  );
}
